import { type ComponentChildren } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocationProvider } from 'preact-iso/router';
import { Router } from './routes/Router';
import { ThemeProvider, useHtmlThemeSync, Spinner } from '@xantory/ui';
import { ToastProvider, ConfirmProvider } from '@xantory/ui';
import { useAuthStore, useThemeStore, useUiStore, syncAuthAcrossTabs } from '@xantory/store';
import { validateSession, useSessionKeepalive } from '@xantory/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

/**
 * Gate first paint on validating a session restored from storage. Without this, the app trusts a
 * persisted `isAuthenticated` flag and would show the dashboard for a stale/invalid/leftover session
 * until some API call happens to 401 — and the landing page may make none. We boot-check once: a bad
 * token is refreshed or (on failure) cleared by the client interceptor, so we land on login instead
 * of a phantom dashboard. A fresh login isn't gated here (useLogin already validates it).
 */
function SessionGate({ children }: { children: ComponentChildren }) {
  // Snapshot boot auth state: only a restored session needs validating; a logged-out boot renders now.
  const [validating, setValidating] = useState(() => useAuthStore.getState().isAuthenticated);

  useEffect(() => {
    if (!useAuthStore.getState().isAuthenticated) {
      setValidating(false);
      return;
    }
    let cancelled = false;
    validateSession()
      // Auth failure → the interceptor already cleared the session; transport errors leave it intact.
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (validating) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Spinner size="lg" />
      </div>
    );
  }
  return <>{children}</>;
}

// Root font-size (rem base), NOT the non-standard `zoom` property this used to
// set on <body>. `zoom` photographically scaled the whole rendered page —
// every px value along with it — which kept px-based and rem-based sizing in
// sync but could also inflate the shell's painted box past the real viewport
// at `lg` (a documented contributor to a scroll-leak bug fixed 2026-09-25).
// A root `font-size` only rescales `rem`-sized CSS: this codebase's
// `typography.*` tokens (already rem) scale correctly, but the many
// still-hardcoded `px` values across `packages/features` do NOT — a known,
// accepted trade-off for this fix, not an oversight. 16px is `reset.css`'s
// own html base, so `md` here must stay '16px' to match it.
const FONT_SIZE_FOR = { sm: '14.4px', md: '16px', lg: '17.6px' } as const;

function AppInner() {
  // R-303: rotate the ACTIVE account's access token just before it lapses, so a
  // console left open all day never pays the 401-refresh-retry stumble on the first
  // action after an idle stretch. Mounted here — above the router — so it survives
  // every navigation. Only the active account is polled; the others lapse by design.
  useSessionKeepalive();
  // R-366: tabs of one browser share the stored session; keep every tab on the pair
  // the session actually runs on, so a stale tab never signs the whole browser out.
  useEffect(() => syncAuthAcrossTabs(), []);
  const resolved = useThemeStore((s) => s.resolved);
  const fontScale = useThemeStore((s) => s.fontScale);
  const toasts = useUiStore((s) => s.toasts);
  const dismiss = useUiStore((s) => s.dismissToast);

  useHtmlThemeSync(resolved);
  useEffect(() => {
    document.documentElement.style.fontSize = FONT_SIZE_FOR[fontScale];
  }, [fontScale]);

  return (
    <LocationProvider>
      <ThemeProvider theme={resolved}>
        <ConfirmProvider>
          <SessionGate>
            <Router />
          </SessionGate>
        </ConfirmProvider>
        <ToastProvider toasts={toasts} onDismiss={dismiss} />
      </ThemeProvider>
    </LocationProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppInner />
    </QueryClientProvider>
  );
}
